import { useTranslation } from 'react-i18next';

import { StatTile } from '@/app/components/ui/StatTile';
import { Skeleton } from '@/app/components/ui/Skeleton';
import type { ParticipationFunnel } from '@/features/participation/api';
import { formatNumber } from '@/lib/format';

const FUNNEL_TILE_KEYS = [
  'invited',
  'activated',
  'active_7d',
  'active_30d',
  'structurally_active',
  'needs_reactivation',
] as const;

const PRIMARY_RATE_KEYS = [
  'daily_sync_pct',
  'ai_coach_use_pct',
  'challenge_participation_pct',
  'notification_open_pct',
] as const;

const SECONDARY_RATE_KEYS = ['break_adoption_pct', 'focus_rhythm_pct'] as const;

/**
 * The 10 headline figures (6 funnel steps + the 4 primary engagement rates)
 * as one tile grid, plus a smaller secondary row for the 2 rates
 * (Pauze-adoptie/Focusritme) that don't fit the 10-tile layout without
 * dropping data.
 */
export function SummaryTiles({ data }: { data: ParticipationFunnel | undefined }) {
  const { t } = useTranslation();

  if (data === undefined) {
    return <Skeleton kind="card" />;
  }

  return (
    <div className="flex flex-col gap-3" data-testid="participation-summary">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {FUNNEL_TILE_KEYS.map((key) => {
          const step = data.funnel.find((s) => s.key === key);
          return (
            <StatTile
              key={key}
              value={formatNumber(step?.count ?? 0)}
              label={t(`participation.funnel.steps.${key}`)}
            />
          );
        })}
        {PRIMARY_RATE_KEYS.map((key) => (
          <StatTile
            key={key}
            value={
              data.rates[key] === null
                ? t('participation.status.noData')
                : `${formatNumber(data.rates[key], { maximumFractionDigits: 0 })}%`
            }
            label={t(`participation.rates.keys.${key}`)}
          />
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3 sm:w-1/2 sm:grid-cols-2 lg:w-2/5">
        {SECONDARY_RATE_KEYS.map((key) => (
          <StatTile
            key={key}
            value={
              data.rates[key] === null
                ? t('participation.status.noData')
                : `${formatNumber(data.rates[key], { maximumFractionDigits: 0 })}%`
            }
            label={t(`participation.rates.keys.${key}`)}
          />
        ))}
      </div>
    </div>
  );
}
